import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import PublicNav from "../components/PublicNav";
import { APPLICATION_RANGES } from "../lib/plans";
import { submitSalesLead, getCaptchaConfig } from "../lib/api";
import CaptchaTurnstile from "../components/CaptchaTurnstile";
import { captureUtmFromUrl, getVisitorSessionId, trackVisitorEvent } from "../lib/visitorAnalytics";
import { useEffect } from "react";

export default function ContactSales() {
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [applicationRange, setApplicationRange] = useState("<25");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: null });
  const plan = (params.get("plan") || "enterprise").toLowerCase();

  useEffect(() => {
    captureUtmFromUrl();
    trackVisitorEvent("enterprise_contact_started", { plan });
    getCaptchaConfig()
      .then(({ data }) => setCaptchaConfig(data))
      .catch(() => setCaptchaConfig({ enabled: false, site_key: null }));
  }, [plan]);

  const captchaRequired = Boolean(captchaConfig.enabled && captchaConfig.site_key);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitSalesLead({
        name,
        email: email.trim(),
        company,
        job_title: jobTitle || undefined,
        application_range: applicationRange,
        interested_plan: plan === "professional" ? "professional" : "enterprise",
        message: message || undefined,
        source: "contact_sales",
        visitor_session_id: getVisitorSessionId(),
        utm: captureUtmFromUrl(),
        captcha_token: captchaRequired ? captchaToken : undefined,
      });
      trackVisitorEvent("sales_lead_submitted", { plan });
      setOk(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not submit your request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-screen login-screen-auth" data-testid="contact-sales-page">
      <div className="login-grid" />
      <PublicNav />
      <section className="login-hero">
        <div className="eyebrow"><span className="signal" /> ENTERPRISE</div>
        <h1>Contact <em>Sales</em></h1>
        <p>Tell us about your estate. We will follow up — no payment is collected on this form.</p>
        <Link className="text-button" to="/pricing">View pricing</Link>
      </section>
      <form className="login-panel" onSubmit={submit}>
        <div className="brand-mark">YUG<span>360</span><small>— MIND</small></div>
        <h2>Contact Sales</h2>
        {ok ? (
          <p className="muted" data-testid="contact-sales-success">
            Thank you. Your request was received. We will contact you at your business email.
          </p>
        ) : (
          <>
            <label>Name
              <input data-testid="contact-sales-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>Business email
              <input data-testid="contact-sales-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>Company
              <input data-testid="contact-sales-company" required value={company} onChange={(e) => setCompany(e.target.value)} />
            </label>
            <label>Job title (optional)
              <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            </label>
            <label>Approximate applications
              <select data-testid="contact-sales-range" value={applicationRange} onChange={(e) => setApplicationRange(e.target.value)}>
                {APPLICATION_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label>Message (optional)
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
            </label>
            {captchaRequired && (
              <CaptchaTurnstile
                siteKey={captchaConfig.site_key}
                onToken={setCaptchaToken}
                onExpire={() => setCaptchaToken("")}
              />
            )}
            <div className="login-actions">
              <button data-testid="contact-sales-submit" className="primary-button full" type="submit" disabled={loading || (captchaRequired && !captchaToken)}>
                {loading ? "Sending…" : "Submit request"} <ArrowUpRight size={16} />
              </button>
            </div>
            {error && <div className="error-message" data-testid="contact-sales-error">{error}</div>}
          </>
        )}
      </form>
    </main>
  );
}
